/* eslint no-use-before-define: ["error", { "variables": false }] */

import * as React from "react";

import { useAppState } from "hooks/AppContext";
import { subNavItems } from "hooks/subNavStatic";

import styles from "./subNav.module.scss";

interface Item {
    uri: string;
    title: string;
    children?: Item[];
}

export interface BreadCrumb {
    title: string;
    uri: string;
}

export interface SubNavProps {
    items: Item[],
    breadCrumbs: BreadCrumb[];
}

/**
 * Is the current elements title contained within the breadcrumb list.
 */
const isActive = (breadCrumbs: BreadCrumb[] = [], title: string) => {
    const str = breadCrumbs?.some((crumb: BreadCrumb) => crumb?.title?.toLowerCase() === title?.toLowerCase())
        ? styles.subNavActive
        : "";
    return str;
};

const getLinkOrText = (title: string, uri?: string, activeClass?: string) => {
    const textClass = `${styles.linkText} ${activeClass}`;
    if (uri) {
        return (
            <a className={styles.navLink} href={uri}>
                <span className={textClass}>{title}</span>
            </a>
        );
    }
    // adding tabIndex here so that this item can gain focus.
    return (
        <span className={styles.navLink}>
            <span className={textClass}>{title}</span>
        </span>
    );
};

/**
 * Gets the list of fly-out elements.
 */
const getChildren = (items: Item[], breadCrumbs: BreadCrumb[] = [], isGrandChild?: boolean) => {
    const children = items.length
        ? items.map((item: Item) => getChild(item, breadCrumbs, isGrandChild))
        : null;
    return children;
};

/**
 * Renders the top level nav items.
 */
const getTopMenu = (items: Item[] = [], breadCrumbs: BreadCrumb[] = []) => items.map((item: Item) => {
    const { uri, title, children } = item;
    const activeClass = isActive(breadCrumbs, title);
    const allLink = uri ? getLinkOrText(`View all in ${title} >`, uri, "") : null;
    return (
        <li key={title} role="menuitem" className={styles.menuItem} tabIndex={0}>
            {getLinkOrText(title, "", activeClass)}
            {
                children && children.length
                    ? (
                        <ul role="menu" className={styles.menuGroup}>
                            {getChildren(children, breadCrumbs)}
                            <li className={styles.menuTitle}>{allLink}</li>
                        </ul>
                    )
                    : null
            }
        </li>
    );
});

/**
 * Renders the fly-outs
 */
const getChild = (item: Item, breadCrumbs: BreadCrumb[] = [], isGrandChild?: boolean) => {
    const { uri, title, children } = item;
    const activeClass = isActive(breadCrumbs, title);
    const nestedClass = isGrandChild ? styles.menuItemNested : "";
    return (
        <React.Fragment key={title}>
            <li role="menuitem" className={nestedClass}>
                {getLinkOrText(title, uri, activeClass)}
            </li>
            {children && children.length ? getChildren(children, breadCrumbs, true) : null}
        </React.Fragment>
    );
};

/**
 * Renders a SubNav module
 */
export const SubNav = () => {
    const { article, pageMetaData: { pathName } } = useAppState();

    const breadCrumbs = article?.breadcrumbLinks;
    const crumb = article?.breadcrumbLink || undefined;
    const { items } = subNavItems;
    const page = {
        title: article?.title || "",
        uri: pathName
    };

    if (crumb) {
        breadCrumbs.push(crumb);
    }
    // the page should always be last.
    breadCrumbs.push(page);

    return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
        <nav role="menubar" className={styles.subNav} aria-label="Sub Navigation">
            <ul role="menu" className={styles.menu}>
                { getTopMenu(items, breadCrumbs) }
            </ul>
        </nav>
    );
};
